import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Select } from 'antd';
import { Descriptions, DescriptionsRow, Divider } from 'common/components';
import { countFormatter, priceFormatter, showSuccessMessage } from 'common/helpers';
import { AuthHooks, UserHooks } from 'common/hooks';
import { ArrowDownShort, Plus } from 'common/svg';
import { SELECT_LIST_HEIGHT_320, SELECT_LIST_HEIGHT_334 } from 'common/config';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  getBasketAddressOptions,
  getBasketDeliveryType,
  getBasketGeneralInformation,
  getBasketLegalEntityOptions,
  getBasketSubdivisionOptions,
} from 'entities/Basket/Basket.helper';
import { EDeliveryType, IBasket, IBasketGoods } from 'entities/Basket/Basket.models';
import { selectBasketDelivery } from 'entities/Basket/Basket.slice';
import {
  addSubdivisionAddress,
  getSubdivisionById,
  selectIsSubdivisionLoading,
  selectSubdivision,
  selectSubdivisionList,
} from 'entities/Subdivision/Subdivision.slice';
import { selectLegalList } from 'entities/LegalEntity/Legal.slice';
import { selectCurrentUser } from 'entities/User/User.slice';
import { AddressCreateCard } from 'entities/Address/components';
import { IAddress, ICreateAddressPayload } from 'entities/Address/Address.models';
import { createAddress, selectIsAddressLoading } from 'entities/Address/Address.slice';
import { ISubdivision } from 'entities/Subdivision/Subdivision.models';

interface IComponentProps {
  basket: IBasket;
  selectedGoods: IBasketGoods[];
  isLimitExceeded: boolean;
  onSubdivisionChange: (subdivision: ISubdivision) => void;
  onLegalChange: (legalId: number) => void;
  onAddressChange: (address: IAddress) => void;
}

export const BasketGeneralInformation: FC<IComponentProps> = ({
  basket,
  selectedGoods,
  isLimitExceeded,
  onSubdivisionChange,
  onLegalChange,
  onAddressChange,
}) => {
  const dispatch = useAppDispatch();

  const basketDelivery = useAppSelector(selectBasketDelivery);
  const currentUser = useAppSelector(selectCurrentUser);
  const legalList = useAppSelector(selectLegalList);
  const subdivision = useAppSelector(selectSubdivision);
  const subdivisionList = useAppSelector(selectSubdivisionList);
  const isAddressLoading = useAppSelector(selectIsAddressLoading);
  const isSubdivisionLoading = useAppSelector(selectIsSubdivisionLoading);

  const [subdivisionId, setSubdivisionId] = useState<number | null>(null);
  const [addressId, setAddressId] = useState<number | null>(null);
  const [legalId, setLegalId] = useState<number | null>(null);
  const [openCreateAddressCard, setOpenCreateAddressCard] = useState<boolean>(false);
  const createAddressInFlightRef = useRef<boolean>(false);
  const { isSeller } = AuthHooks.useIsSeller();
  const { isAccountAdmin } = UserHooks.useCurrentUserRole();
  const { count, amount, totalAmount, vatAmount, deliveryAmount, benefit } = useMemo(
    () => getBasketGeneralInformation({ goods: selectedGoods, basket, delivery: basketDelivery }),
    [selectedGoods, basket, basketDelivery],
  );

  useEffect(() => {
    if (!basket) {
      return;
    }

    if (basket.subdivision) {
      setSubdivisionId(basket.subdivision.id);
    }

    if (basket.addressId) {
      setAddressId(basket.addressId);
    } else {
      setAddressId(null);
    }

    if (basket.legalId) {
      setLegalId(basket.legalId);
    } else {
      setLegalId(null);
    }
  }, [basket]);

  const address = basket.store ? basket.store.address : basket.address?.name;
  const subdivisionOptions = getBasketSubdivisionOptions(subdivisionList);
  const addressOptions = getBasketAddressOptions(subdivision?.addresses, addressId);
  const legalOptions = getBasketLegalEntityOptions(legalList);
  const { isDelivery } = getBasketDeliveryType(basket);
  const allowToChangeAddress = !isSeller && basket.deliveryType !== EDeliveryType.PickUp;
  const allowToCreateAddress = !isSeller && isAccountAdmin;

  const changeSubdivision = async (subdivisionId: number): Promise<void> => {
    const subdivisionResponse = await dispatch(getSubdivisionById({ id: subdivisionId }));

    if (!subdivisionResponse) {
      return;
    }

    setSubdivisionId(subdivisionResponse.id);
    onSubdivisionChange(subdivisionResponse);
  };

  const changeLegal = async (legalId: number): Promise<void> => {
    setLegalId(legalId);
    onLegalChange(legalId);
  };

  const changeAddress = async (addressId: number): Promise<void> => {
    const newAddress = subdivision?.addresses.find((i) => i.id === addressId);

    if (!newAddress) {
      return;
    }

    setAddressId(newAddress.id);
    onAddressChange(newAddress);
  };

  const handleCreateAddress = async ({ onSuccess, ...rest }: ICreateAddressPayload): Promise<void> => {
    if (!subdivision) {
      return;
    }

    // Синхронный guard от двойного сабмита: loading-state асинхронен, даблклик его проскакивает
    // (создаются два адреса + гонка main в подразделении).
    if (createAddressInFlightRef.current) {
      return;
    }
    createAddressInFlightRef.current = true;

    try {
      const address = await dispatch(
        createAddress({ ...rest, onSuccess: () => showSuccessMessage('Новый адрес добавлен в аккаунт.') }),
      );

      if (!address) {
        return;
      }

      await dispatch(
        addSubdivisionAddress({
          subdivisionId: subdivision.id,
          addressId: address.id,
          onSuccess: () => showSuccessMessage('Новый адрес добавлен в подразделение.'),
        }),
      );

      onSuccess?.();
      setOpenCreateAddressCard(false);
      setAddressId(address.id);
      onAddressChange(address);
    } finally {
      createAddressInFlightRef.current = false;
    }
  };

  return (
    <div>
      <div className="mb-6 ff-medium fs-m">Общая информация</div>

      <Descriptions>
        <DescriptionsRow
          label="Подразделение (место возникновения затрат)"
          value={
            isSeller ? (
              <span className="ff-regular fs-s">{basket.subdivision?.name}</span>
            ) : (
              <Select
                rootClassName="redesign"
                popupClassName="basket-order-details__body-block-select-popup"
                className="basket-order-details__body-block-select"
                options={subdivisionOptions}
                value={subdivisionId}
                onChange={changeSubdivision}
                suffixIcon={<ArrowDownShort className="icon-arrow-down-short-dark-grey" />}
                listHeight={SELECT_LIST_HEIGHT_334}
                virtual={false}
                popupMatchSelectWidth={false}
                placeholder="Выберите подразделение"
                notFoundContent={<span className="text-lists color-dark-grey">В списке нет вариантов.</span>}
              />
            )
          }
        />
        <DescriptionsRow
          label="Юридическое лицо"
          value={
            isSeller ? (
              <span className="ff-regular fs-s">{basket.legal?.name}</span>
            ) : (
              <Select
                rootClassName="redesign"
                popupClassName="basket-order-details__body-block-select-popup"
                className="basket-order-details__body-block-select"
                options={legalOptions}
                value={legalId}
                onChange={changeLegal}
                suffixIcon={<ArrowDownShort className="icon-arrow-down-short-dark-grey" />}
                listHeight={SELECT_LIST_HEIGHT_320}
                virtual={false}
                popupMatchSelectWidth={false}
                placeholder="Выберите юр.лицо"
                notFoundContent={<span className="text-lists color-dark-grey">В списке нет вариантов.</span>}
              />
            )
          }
        />
        {!isSeller && (
          <DescriptionsRow
            label="Город"
            value={
              <span className={`ff-regular fs-s ${!currentUser?.city ? 'color-dark-grey' : 'color-black'}`}>
                {currentUser?.city?.name ?? 'Не выбран'}
              </span>
            }
          />
        )}
        <DescriptionsRow
          label="Адрес"
          value={
            !allowToChangeAddress ? (
              <span className={`ff-regular fs-s ${!address ? 'color-red' : 'color-black'}`}>{address}</span>
            ) : (
              <Select
                rootClassName="redesign"
                popupClassName="basket-order-details__body-block-select-popup"
                className="basket-order-details__body-block-select"
                options={addressOptions}
                value={addressId}
                onChange={changeAddress}
                suffixIcon={<ArrowDownShort className="icon-arrow-down-short-dark-grey" />}
                listHeight={SELECT_LIST_HEIGHT_334}
                virtual={false}
                popupMatchSelectWidth={false}
                optionLabelProp="desc"
                placeholder="Выберите адрес"
                notFoundContent={<span className="text-lists color-dark-grey">В списке нет вариантов.</span>}
                dropdownRender={(menu) =>
                  allowToCreateAddress ? (
                    <>
                      <Button
                        className="button-text dark-green"
                        icon={<Plus className="icon-plus-dark-green" />}
                        onClick={() => setOpenCreateAddressCard(true)}
                      >
                        Добавить адрес
                      </Button>

                      <Divider marginY={20} />

                      {menu}
                    </>
                  ) : (
                    menu
                  )
                }
              />
            )
          }
        />
        <DescriptionsRow label="Товаров в заказе" value={countFormatter(count)} />
        <DescriptionsRow label="Стоимость товаров" value={priceFormatter(amount)} />
        {isDelivery && (
          <DescriptionsRow
            label="Стоимость доставки"
            value={
              <div>
                <div>{priceFormatter(deliveryAmount)}</div>
                <div className="ff-regular fs-xs color-dark-grey">
                  Указанная стоимость является ориентировочной. Точную цену сообщит менеджер при подтверждении заказа.
                </div>
              </div>
            }
          />
        )}
        <DescriptionsRow label="Сумма НДС" value={priceFormatter(vatAmount)} />
        <DescriptionsRow
          label="Итого с НДС"
          value={priceFormatter(totalAmount)}
          color={isLimitExceeded ? '#df4058' : undefined}
        />
        {benefit > 0 && <DescriptionsRow label="Ваша выгода" value={priceFormatter(benefit)} color="#69a443" />}
      </Descriptions>

      <AddressCreateCard
        open={openCreateAddressCard}
        loading={isAddressLoading || isSubdivisionLoading}
        address={null}
        onCancel={() => setOpenCreateAddressCard(false)}
        onSubmit={handleCreateAddress}
      />
    </div>
  );
};
